import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import { SocketEventTypes } from "entities/ISocketArgs";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Socket, io } from "socket.io-client";
import { socketUrl } from "util/constants";

type WebSocketProviderProps = {
  children: ReactNode;
};

type SocketEventHandler<T> = (data: T) => void;

interface WebSocketContextData {
  socket: Socket | null;
  addEventHandler: <K extends keyof SocketEventTypes>(
    eventType: K,
    handler: SocketEventHandler<SocketEventTypes[K]>
  ) => void;
  removeEventHandler: <K extends keyof SocketEventTypes>(
    eventType: K,
    handler: SocketEventHandler<SocketEventTypes[K]>
  ) => void;
}

const WebSocketContext = createContext<WebSocketContextData>({
  socket: null,
  addEventHandler: () => {},
  removeEventHandler: () => {},
});

function buildExtraHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  if (process.env.REACT_APP_SENTRY_RELEASE) {
    headers["App-Version"] = process.env.REACT_APP_SENTRY_RELEASE;
  }

  if (process.env.REACT_APP_BUILD_NUMBER) {
    headers["Build-Number"] = process.env.REACT_APP_BUILD_NUMBER;
  }

  return headers;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { getAccessTokenSilently }: Auth0ContextInterface = useAuth0();

  // Ref so the auth callback always calls the latest function without
  // needing to tear down / recreate the socket.
  const getTokenRef = useRef(getAccessTokenSilently);

  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  // The socket instance is created once and never replaced.
  // Socket.IO handles reconnection, backoff, and transport fallback natively.
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Handlers registered before the socket instance exists. Socket creation is
  // deferred (idle callback + token fetch), so consumers mounting during that
  // window — e.g. Chat on a direct inbox load — would otherwise have their
  // addEventHandler calls silently dropped and never receive realtime events
  // (their registration effects have stable deps and do not re-run when the
  // socket appears). Buffered here and attached on creation instead.
  const pendingHandlersRef = useRef<[string, SocketEventHandler<never>][]>([]);

  useEffect(() => {
    // Fetch an initial token, then create the socket once.
    let cancelled = false;
    let idleHandle: number | undefined;

    // Defer opening the socket until the browser is idle so the connection (and
    // Socket.IO's polling→websocket handshake) doesn't compete with the critical
    // bundle and the initial /agents/me + /merchants requests during first paint.
    // This matters most on high-latency / proxied corporate networks where the
    // browser's small connection pool is the bottleneck. The 3s timeout bounds
    // how long realtime updates are delayed when the main thread stays busy.
    const connect = () => {
      if (cancelled) return;

      getTokenRef.current().then((token) => {
        if (cancelled) return;

        const instance = io(socketUrl, {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
            ...buildExtraHeaders(),
          },

          // Let Socket.IO handle reconnection natively. Backoff is capped at 30s
          // so a network that blocks websockets cannot tighten into a reconnect
          // storm against the socket host.
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1_000,
          reconnectionDelayMax: 30_000,
          randomizationFactor: 0.5,
        });

        socketRef.current = instance;

        for (const [eventType, handler] of pendingHandlersRef.current) {
          instance.on(eventType, handler as any);
        }
        pendingHandlersRef.current = [];

        setSocket(instance);
      });
    };

    if (typeof requestIdleCallback === "function") {
      idleHandle = requestIdleCallback(connect, { timeout: 3_000 });
    } else {
      idleHandle = window.setTimeout(connect, 0) as unknown as number;
    }

    return () => {
      cancelled = true;

      if (idleHandle !== undefined) {
        if (typeof cancelIdleCallback === "function") {
          cancelIdleCallback(idleHandle);
        } else {
          clearTimeout(idleHandle);
        }
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Handle auth-related connection failures.
  //
  // Native reconnection works great for transient network drops (token still
  // valid). But after a long background period (common on mobile — swipe away,
  // come back hours later) the token may have expired. Socket.IO will keep
  // retrying with the stale token and never succeed.
  //
  // Strategy: on connect_error, proactively refresh the token and patch
  // extraHeaders *before* the next attempt fires. Because getAccessTokenSilently
  // is async, we pause native reconnection, refresh, then resume — guaranteeing
  // the fresh token is in place.
  useEffect(() => {
    if (!socket) return;

    const handleConnectError = (_error: Error) => {
      // Pause native reconnection so we can swap the token first
      socket.io.opts.reconnection = false;

      getTokenRef
        .current()
        .then((freshToken) => {
          (
            socket.io.opts.extraHeaders as Record<string, string>
          ).Authorization = `Bearer ${freshToken}`;
        })
        .catch(() => {
          // Token refresh failed (e.g. refresh token expired, user logged out).
          // Re-enable reconnection anyway — the next cycle will try again.
        })
        .finally(() => {
          // Re-enable native reconnection and kick off the next attempt
          socket.io.opts.reconnection = true;
          socket.connect();
        });
    };

    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off("connect_error", handleConnectError);
    };
  }, [socket]);

  // Stable add/remove helpers. Because socketRef is a mutable ref
  // and the socket instance never changes, these functions keep a
  // permanent identity — consumers never need to re-register.
  const addEventHandler = useRef(
    <K extends keyof SocketEventTypes>(
      eventType: K,
      handler: SocketEventHandler<SocketEventTypes[K]>
    ) => {
      if (!socketRef.current) {
        pendingHandlersRef.current.push([
          eventType,
          handler as SocketEventHandler<never>,
        ]);
        return;
      }
      socketRef.current.on(eventType, handler as any);
    }
  ).current;

  const removeEventHandler = useRef(
    <K extends keyof SocketEventTypes>(
      eventType: K,
      handler: SocketEventHandler<SocketEventTypes[K]>
    ) => {
      pendingHandlersRef.current = pendingHandlersRef.current.filter(
        ([pendingEventType, pendingHandler]) =>
          pendingEventType !== eventType || pendingHandler !== handler
      );
      if (!socketRef.current) return;
      socketRef.current.off(eventType, handler as any);
    }
  ).current;

  return (
    <WebSocketContext.Provider
      value={{ socket, addEventHandler, removeEventHandler }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export const useWebSocket = () => useContext(WebSocketContext);
